// components/HubSpotSyncLogsModal.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const HubSpotSyncLogsModal = ({ isOpen, onClose }) => {
  const { isDarkMode } = useTheme();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchLogs(1);
    }
  }, [isOpen]);

  const fetchLogs = async (page) => {
    setLoading(true);
    try {
      const response = await apiService.getHubSpotSyncLogs({
        page,
        limit: 10,
      });
      setLogs(response.logs);
      setCurrentPage(response.currentPage);
      setTotalPages(response.pages);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSyncIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "failed":
        return <XCircle className="w-5 h-5 text-red-500" />;
      case "in_progress":
      case "started":
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getSyncTypeBadge = (type) => {
    const colors = {
      full: isDarkMode
        ? "bg-purple-900/30 text-purple-300"
        : "bg-purple-100 text-purple-700",
      contacts: isDarkMode
        ? "bg-blue-900/30 text-blue-300"
        : "bg-blue-100 text-blue-700",
      deals: isDarkMode
        ? "bg-green-900/30 text-green-300"
        : "bg-green-100 text-green-700",
      tickets: isDarkMode
        ? "bg-yellow-900/30 text-yellow-300"
        : "bg-yellow-100 text-yellow-700",
      tasks: isDarkMode
        ? "bg-pink-900/30 text-pink-300"
        : "bg-pink-100 text-pink-700",
    };

    return (
      <span
        className={`px-2 py-1 rounded text-xs ${colors[type] || colors.full}`}
      >
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (startDate, endDate) => {
    if (!endDate) return "In progress";
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = end - start;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
      <div
        className={`rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isDarkMode ? "bg-white" : "bg-gray-100 border border-gray-200"
              }`}
            >
              <img
                src="/assets/hubspot.png"
                alt="HubSpot"
                className="w-6 h-6"
              />
            </div>
            <div>
              <h2
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Sync History
              </h2>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                View all HubSpot sync operations
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw
                className={`w-8 h-8 animate-spin ${
                  isDarkMode ? "text-purple-500" : "text-purple-600"
                }`}
              />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Clock
                className={`w-12 h-12 mb-3 ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              />
              <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                No sync history yet
              </p>
              <p
                className={`text-sm mt-1 ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                Run your first sync to see the logs here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className={`rounded-lg p-4 cursor-pointer transition-colors ${
                    isDarkMode
                      ? "bg-[#1a1d23] hover:bg-[#22262F]"
                      : "bg-gray-50 hover:bg-gray-100"
                  }`}
                  onClick={() =>
                    setSelectedLog(selectedLog?.id === log.id ? null : log)
                  }
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      {getSyncIcon(log.status)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {getSyncTypeBadge(log.syncType)}
                          <span
                            className={`text-xs ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            {formatDate(log.startedAt)}
                          </span>
                        </div>

                        <div
                          className={`text-sm mb-2 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Duration:{" "}
                          {formatDuration(log.startedAt, log.completedAt)}
                        </div>

                        {log.results && (
                          <div className="flex items-center gap-4 text-xs flex-wrap">
                            <span className="text-green-500">
                              ✓ {log.results.imported} imported
                            </span>
                            <span className="text-blue-500">
                              ↻ {log.results.updated} updated
                            </span>
                            {log.results.failed > 0 && (
                              <span className="text-red-500">
                                ✗ {log.results.failed} failed
                              </span>
                            )}
                          </div>
                        )}

                        {selectedLog?.id === log.id &&
                          log.results?.errors &&
                          log.results.errors.length > 0 && (
                            <div
                              className={`mt-3 p-3 rounded ${
                                isDarkMode
                                  ? "bg-red-900/10 border border-red-900/30"
                                  : "bg-red-50 border border-red-200"
                              }`}
                            >
                              <div
                                className={`text-xs font-medium mb-2 ${
                                  isDarkMode ? "text-red-400" : "text-red-600"
                                }`}
                              >
                                Errors:
                              </div>
                              <div className="space-y-1 max-h-40 overflow-y-auto">
                                {log.results.errors
                                  .slice(0, 5)
                                  .map((err, idx) => (
                                    <div
                                      key={idx}
                                      className={`text-xs ${
                                        isDarkMode
                                          ? "text-red-300/80"
                                          : "text-red-500/80"
                                      }`}
                                    >
                                      • {err.error}
                                    </div>
                                  ))}
                                {log.results.errors.length > 5 && (
                                  <div
                                    className={`text-xs mt-1 ${
                                      isDarkMode
                                        ? "text-red-400"
                                        : "text-red-500"
                                    }`}
                                  >
                                    ... and {log.results.errors.length - 5} more
                                    errors
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                        {log.errorMessage && (
                          <div
                            className={`mt-2 text-xs ${
                              isDarkMode ? "text-red-400" : "text-red-600"
                            }`}
                          >
                            Error: {log.errorMessage}
                          </div>
                        )}
                      </div>
                    </div>

                    {log.user && (
                      <div
                        className={`text-xs ml-2 ${
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        by {log.user.name}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            className={`px-6 py-4 border-t flex items-center justify-between ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div
              className={`text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchLogs(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? "bg-[#1a1d23] text-gray-400 hover:text-white"
                    : "bg-gray-100 text-gray-500 hover:text-gray-700"
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => fetchLogs(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? "bg-[#1a1d23] text-gray-400 hover:text-white"
                    : "bg-gray-100 text-gray-500 hover:text-gray-700"
                }`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HubSpotSyncLogsModal;
